<script setup lang="ts" generic="T">
import { DialogDescription, DialogTitle } from "@headlessui/vue";
import { type Component, computed, type Ref, ref, watch } from "vue";

import Tooltip from "~/components/dumb/Tooltip.vue";
import { XIcon } from "~/icons";
import { IconMode, ModalPosition } from "~/shared/enums";
import { usePageStore } from "~/stores";

const props = defineProps<{
  entity: T;
  title: string | ((t: T) => string);
  hideTitle: boolean | undefined;
  emphasizeTitle: boolean | undefined;
  description: string | ((t: T) => string) | undefined;
  position: ModalPosition | undefined;
  iconMode: IconMode | undefined;
  closeText: string | undefined;
  customStyles: string | undefined;
  closeIconStyles: string | undefined;
  titleIcon: Component | undefined;
  titleIconArgs: object | ((t: T) => object) | undefined;
  widthStyle: Record<string, string>;
  hasMobileSwipe: boolean | undefined;
  overflowHidden: boolean | undefined;
}>();

const emit = defineEmits<{
  close: [];
}>();

const pageStore = usePageStore();

const positionLeft = computed(() => props.position === ModalPosition.LEFT);
const iconMode = computed(() => props.iconMode ?? IconMode.NONE);

const displayedEntity = ref(props.entity) as Ref<T>;
const normCloseText = computed(() => props.closeText ?? "Close");
const opening = ref(!!props.entity);

const positionBottom = computed(
  () =>
    props.position === ModalPosition.BOTTOM ||
    (pageStore.isMobile && (props.position === ModalPosition.RIGHT || props.position === undefined))
);

const realizeStr = (unrealized?: string | ((t: T) => string)) => {
  if (typeof unrealized === "function") {
    return unrealized(displayedEntity.value);
  }
  return unrealized;
};

const displayedTitle = ref(realizeStr(props.title));
const displayedDescription = ref(realizeStr(props.description));

const realizedTitleIconArgs = computed(() => {
  if (props.titleIconArgs === undefined) {
    return {};
  }
  if (typeof props.titleIconArgs === "function") {
    return props.titleIconArgs(displayedEntity.value);
  }
  return props.titleIconArgs;
});

watch(
  () => props.entity,
  (newEntity) => {
    if (!newEntity) {
      return;
    }
    displayedEntity.value = newEntity;
    opening.value = true;
    // oxlint-disable-next-line dart/no-set-timeout
    setTimeout(() => {
      opening.value = false;
    });
  },
  { immediate: true }
);

watch([() => props.title, () => displayedEntity], ([newValue]) => {
  displayedTitle.value = realizeStr(newValue);
});

watch(
  () => props.description,
  (newDescription) => {
    displayedDescription.value = realizeStr(newDescription);
  }
);
</script>

<template>
  <div
    v-if="!opening"
    class="flex w-full flex-col rounded-lg bg-std text-left align-middle app-drag-none"
    :class="[
      customStyles,
      hasMobileSwipe && 'h-full',
      pageStore.isMobile && positionBottom && '!rounded-2xl !rounded-b-none',
      !hasMobileSwipe && 'pt-4',
      pageStore.isMobile ? 'px-4 pb-2' : 'px-6 pb-6',
      pageStore.isMobile && !overflowHidden && 'overflow-x-hidden overflow-y-auto',
    ]"
    :style="widthStyle">
    <!-- Top handle for swiping -->
    <div v-if="hasMobileSwipe" class="pointer-events-none z-20 flex w-full justify-center py-2">
      <div class="h-1 w-24 rounded-full bg-hvy" />
      <div class="absolute -top-6 h-6 w-full" />
    </div>

    <XIcon class="sr-only" />

    <!-- Header -->
    <div class="mr-3 mb-1 flex items-center gap-3" :class="hideTitle && '!sr-only'">
      <div
        v-if="titleIcon"
        :class="{
          'mt-2 mr-1 flex size-10 shrink-0 items-center justify-center rounded-full': iconMode !== IconMode.NONE,
          'bg-danger-base/30 text-danger-base': iconMode === IconMode.DELETE,
          'bg-primary-base/30 text-primary-base': iconMode === IconMode.PRIMARY,
          'contents text-lt': iconMode === IconMode.NONE,
        }">
        <component :is="titleIcon" v-bind="realizedTitleIconArgs" class="icon-md" />
      </div>
      <DialogTitle
        as="h3"
        class="break-words hyphens-auto text-md select-none"
        :class="emphasizeTitle ? 'text-3xl font-bold' : 'text-lg font-semibold'">
        {{ displayedTitle }}
      </DialogTitle>
    </div>
    <DialogDescription
      v-if="displayedDescription"
      class="mb-1 text-sm text-lt select-none"
      :class="[iconMode !== IconMode.NONE && titleIcon && 'ml-[56px]', hideTitle && '!sr-only']">
      {{ displayedDescription }}
    </DialogDescription>

    <!-- Content -->
    <slot :entity="displayedEntity" />

    <!-- Footer -->
    <div v-if="$slots.footerLeft || $slots.actions" class="mt-3 flex justify-between">
      <slot v-if="$slots.footerLeft" name="footerLeft" :entity="displayedEntity" />
      <div v-else />

      <slot v-if="$slots.actions" name="actions" :entity="displayedEntity" />
      <div v-else />
    </div>

    <!-- Top controls -->
    <div
      v-if="!pageStore.isMobile"
      class="absolute top-3 flex"
      :class="positionLeft ? 'left-[calc(100%+12px)]' : 'right-3'">
      <Tooltip :text="normCloseText">
        <div
          class="flex cursor-pointer items-center rounded-sm p-0.5 focus-ring-std"
          :class="[positionLeft ? 'hover-bg-strong text-white' : 'text-vlt hover:bg-md', closeIconStyles]"
         
          @click="emit('close')"
          @keydown.enter="emit('close')">
          <XIcon class="icon-sm" />
        </div>
      </Tooltip>
    </div>
  </div>
</template>
